import React, { useEffect } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useDispatch } from "react-redux";
import { setSelectedComponent } from "./QuestionnaryFormSlice";

function SortableItem({ sectionItem, sectionIndex, selectedSection, selectedPage, setSelectedSection, setSelectedPage, dropdownOpen, setDropdown, handleSectionScroll, handlePageScroll, handleDropdown, formStatus, setComplianceClick }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: sectionItem.section_id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li ref={setNodeRef} style={style} {...attributes}>
      <div
        onClick={() => {
          setSelectedSection(sectionItem?.section_id);
          setSelectedPage(null);
          handleDropdown(sectionItem?.section_id);
          handleSectionScroll(sectionIndex, sectionItem?.section_id);
          setComplianceClick(false);
        }}
        className={`${
          selectedSection === sectionItem?.section_id
            ? "bg-[#d1d3d9b7]"
            : "hover:bg-[#EFF1F8]"
        } flex items-center w-full justify-between pl-11 pr-3 cursor-pointer`}
      >
        <div className="flex items-center w-[88%]">
          <img src="/Images/down-arrow.svg" alt="down-arrow" />
          <p
            data-testid={`sidebar-section-${sectionIndex}`}
            title={sectionItem?.section_name}
            className="font-normal text-base text-[#2B333B] ml-3 py-2 truncate"
          >
            {sectionItem?.section_name}
          </p>
        </div>
        <div className="w-[12%] flex items-center">
          {formStatus === "Draft" ? (
            <img
              className="cursor-grab p-2 rounded-full hover:bg-[#FFFFFF] h-9 w-9"
              title="Drag"
              src="/Images/drag.svg"
              alt="Drag"
              {...listeners}
            />
          ) : (
            <img
              className="cursor-not-allowed p-2 rounded-full h-9 w-9"
              title="Drag"
              src="/Images/drag.svg"
              alt="Drag"
            />
          )}
        </div>
      </div>

      {sectionItem?.pages?.length > 0 &&
        sectionItem?.pages.map((pageItem, pageIndex) =>
          dropdownOpen === sectionItem?.section_id ? (
            <div
              key={pageItem?.page_id}
              onClick={() => {
                setSelectedSection(sectionItem?.section_id);
                setSelectedPage(pageIndex);
                handlePageScroll(sectionIndex, pageItem.page_id);
                setComplianceClick(false);
              }}
              className={`${
                selectedSection === sectionItem?.section_id &&
                selectedPage === pageIndex
                  ? "bg-[#d1d3d9b7]"
                  : "hover:bg-[#EFF1F8]"
              } flex items-center pl-14 pr-2 cursor-pointer truncate`}
            >
              <p className="rounded-full min-w-2 h-2 bg-black mr-4"></p>
              <p
                data-testid={`sidebar-section-${sectionIndex}-page-${pageIndex}`}
                title={pageItem?.page_name}
                className="font-normal text-base text-[#2B333B] cursor-pointer py-2 truncate"
              >
                {pageItem?.page_name}
              </p>
            </div>
          ) : null
        )}
    </li>
  );
}

function SideLayout({
  formDefaultInfo,
  sections,
  handlePageScroll,
  selectedSection,
  setSelectedSection,
  selectedPage,
  setSelectedPage,
  dropdownOpen,
  setDropdown,
  onDragEnd,
  formStatus,
  handleAddRemoveSection,
  handleSectionSaveOrder,
  handleSectionScroll,
  complianceLogic,
  setComplianceClick,
}) {
  const dispatch = useDispatch();

  const handleDropdown = (sectionId) => {
    setDropdown(dropdownOpen === sectionId ? "" : sectionId);
  };

  useEffect(() => {
    if (sections?.length > 0) {
      setSelectedSection(sections[0]?.section_id);
      setDropdown(sections[0]?.section_id);
    }
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDnD = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = sections.findIndex((i) => i.section_id === active.id);
      const newIndex = sections.findIndex((i) => i.section_id === over?.id);
      const reordered = arrayMove(sections, oldIndex, newIndex);
      onDragEnd(reordered,true);
    }
  };

  return (
    <div className="py-4">
      <div className="flex items-center px-9 pb-5">
        <img src="/Images/form-name.svg" alt="form-name" />
        <p
          title={formDefaultInfo?.internal_name}
          className="ml-3 font-semibold text-base text-[#2B333B] truncate w-[90%]"
        >
          {formDefaultInfo?.internal_name}
        </p>
      </div>

      <div className="default-sidebar h-customh8">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDnD}>
          <SortableContext items={sections.map((s) => s.section_id)} strategy={verticalListSortingStrategy}>
            <ul>
              {sections?.map((sectionItem, index) => (
                <SortableItem
                  key={sectionItem.section_id}
                  sectionItem={sectionItem}
                  sectionIndex={index}
                  selectedSection={selectedSection}
                  selectedPage={selectedPage}
                  setSelectedSection={setSelectedSection}
                  setSelectedPage={setSelectedPage}
                  dropdownOpen={dropdownOpen}
                  setDropdown={setDropdown}
                  handleSectionScroll={handleSectionScroll}
                  handlePageScroll={handlePageScroll}
                  handleDropdown={handleDropdown}
                  formStatus={formStatus}
                  setComplianceClick={setComplianceClick}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>

        {complianceLogic.length > 0 && (
          <div
            data-testid="compliance-logic"
            className={`pl-11 py-2 cursor-pointer ${
              selectedSection === "compliance" ? "bg-[#d1d3d9b7]" : ""
            }`}
            onClick={() => {
              setComplianceClick(true);
              setSelectedSection("compliance");
              dispatch(setSelectedComponent("compliancelogic"));
            }}
          >
            Compliance Logic
          </div>
        )}

        <button
          onClick={
            formStatus === "Draft"
              ? () => {
                  handleAddRemoveSection("add");
                  handleSectionSaveOrder(sections);
                }
              : null
          }
          data-testid="add-section"
          className={`flex items-center ${
            formStatus === "Draft" ? "" : "cursor-not-allowed"
          } font-semibold text-[#2B333B] text-base mt-5 px-5`}
        >
          <span className="mr-[15px]">+</span>
          Add section
        </button>
      </div>
    </div>
  );
}

export default SideLayout;
